import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader, DataTable, type Column } from '@/components/common';
import { stagger, fadeUp } from '@/lib/animations';
import { wellsApi, type Well, type WellStatus } from '@/api/services/wells.api';
import { getErrorMessage } from '@/api/client';
import { formatCurrency } from '@/lib/utils';

/**
 * The wells the work is done on.
 *
 * A well outlives the jobs performed on it — drilling, completion, workover,
 * each a separate contract and often years apart. This is where they add up,
 * and it is the only place "what has this well cost us" can be asked.
 *
 * Read-only. Wells are created alongside the jobs that reference them, and an
 * editing surface can wait until somebody needs one.
 */

const STATUS_STYLE: Record<WellStatus, string> = {
  PLANNED: 'bg-status-neutral-subtle text-status-neutral-foreground',
  DRILLING: 'bg-status-info-subtle text-status-info-foreground',
  COMPLETING: 'bg-status-info-subtle text-status-info-foreground',
  PRODUCING: 'bg-status-success-subtle text-status-success-foreground',
  SUSPENDED: 'bg-status-warning-subtle text-status-warning-foreground',
  PLUGGED: 'bg-status-neutral-subtle text-status-neutral-foreground',
};

export default function Wells() {
  const navigate = useNavigate();
  const [wells, setWells] = useState<Well[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    wellsApi
      .getAll()
      .then((data) => !cancelled && setWells(data))
      .catch((error) => !cancelled && toast.error(getErrorMessage(error)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const columns: Column<Well>[] = [
    {
      key: 'name',
      header: 'Well',
      sortable: true,
      linkTo: (well) => `/wells/${well.id}`,
      render: (well) => (
        <div>
          <p className="font-medium">{well.name}</p>
          {/* The API number identifies the well to us, the operator and the
              state alike, which is why it is the one we key on. */}
          <p className="font-mono text-xs text-muted-foreground">API {well.apiNumber}</p>
        </div>
      ),
    },
    {
      key: 'operatorName',
      header: 'Operator',
      sortable: true,
      render: (well) => well.operatorName || <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'leaseName',
      header: 'Lease / Field',
      render: (well) => (
        <div className="text-sm">
          <p>{well.leaseName || '—'}</p>
          <p className="text-xs text-muted-foreground">{well.fieldName || well.basin || ''}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (well) => (
        <Badge className={STATUS_STYLE[well.status]}>{well.status.toLowerCase()}</Badge>
      ),
    },
    {
      key: '_count',
      header: 'Jobs',
      className: 'text-right',
      render: (well) => <span className="tabular-nums">{well._count?.jobs ?? 0}</span>,
    },
    {
      key: 'consumedCost',
      header: 'Material consumed',
      className: 'text-right',
      sortable: true,
      render: (well) => (
        <span className="font-medium tabular-nums">{formatCurrency(well.consumedCost ?? 0)}</span>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Wells' }]}
          title="Wells"
          description="Wells under contract, with material consumption totalled across every job performed on each."
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DataTable
          data={wells}
          columns={columns}
          loading={loading}
          pagination
          onRowClick={(well) => navigate(`/wells/${well.id}`)}
          emptyTitle="No wells yet"
          emptyDescription="A well is created with the first job performed on it."
        />
      </motion.div>
    </motion.main>
  );
}
